import { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "../lib/api";
import { PageHeader, Surface, Empty } from "../components/Primitives";
import { Plus, Trash2, Download, Upload, Search, Pencil, Info, PackagePlus, Image as ImageIcon, CloudUpload } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";

export default function Products() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "user";
  const isAdmin = user?.role === "admin";
  // Le bouton "Sync vers prod" est un outil DevOps réservé au compte support
  // (caché du tableau utilisateurs client). La cliente admin ne doit JAMAIS le voir.
  const isSupport = !!user?.is_support;
  const [products, setProducts] = useState([]);
  const [stock, setStock] = useState([]); // [{product_id, site_id, quantity}]
  const [sites, setSites] = useState([]);
  const [filter, setFilter] = useState("");
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", sku: "", barcode: "", category: "", unit: "boîte", price: 0, cost: 0, min_stock: 10 });
  const [importText, setImportText] = useState("");
  const [showImport, setShowImport] = useState(false);
  const [visibleCount, setVisibleCount] = useState(50);
  const [categoryFilter, setCategoryFilter] = useState("");
  const [adjustingProduct, setAdjustingProduct] = useState(null); // product object
  const [adjQty, setAdjQty] = useState("");
  const [adjSiteId, setAdjSiteId] = useState("");
  const [adjBatchNumber, setAdjBatchNumber] = useState("");
  const [adjExpiry, setAdjExpiry] = useState("");
  const [adjWithBatch, setAdjWithBatch] = useState(false);
  const [adjMode, setAdjMode] = useState("add"); // "add" | "remove" | "set"

  const load = async () => {
    const [p, s, si] = await Promise.all([
      api.get("/products"),
      api.get("/stock"),
      api.get("/sites"),
    ]);
    setProducts(p.data); setStock(s.data); setSites(si.data);
  };
  useEffect(() => { load(); }, []);

  // total stock across all sites for each product
  const stockByProduct = useMemo(() => {
    const m = {};
    for (const r of stock) m[r.product_id] = (m[r.product_id] || 0) + (r.quantity || 0);
    return m;
  }, [stock]);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const { data } = await api.post("/products", { ...form, price: Number(form.price), cost: Number(form.cost), min_stock: Number(form.min_stock) });
      const hasImage = !!data.image_url;
      if (hasImage) {
        toast.success("Produit créé");
      } else {
        toast.success("Produit créé. Image IA en cours de génération… (apparaît dans ~10-15s)", { duration: 6000 });
        // Rafraîchit la liste après 15s pour afficher l'image générée
        setTimeout(() => load(), 15000);
      }
      setShowForm(false);
      setForm({ name: "", sku: "", barcode: "", category: "", unit: "boîte", price: 0, cost: 0, min_stock: 10 });
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  // Édition complète d'un produit existant (nom, SKU, prix, etc.)
  const [editProduct, setEditProduct] = useState(null);
  const openEdit = (p) => {
    setEditProduct({
      id: p.id,
      name: p.name || "",
      sku: p.sku || "",
      barcode: p.barcode || "",
      category: p.category || "",
      unit: p.unit || "boîte",
      price: p.price || 0,
      cost: p.cost || 0,
      min_stock: p.min_stock || 0,
    });
  };
  const submitEdit = async (e) => {
    e.preventDefault();
    if (!editProduct) return;
    try {
      const { id, ...payload } = editProduct;
      await api.put(`/products/${id}`, {
        ...payload,
        price: Number(payload.price),
        cost: Number(payload.cost),
        min_stock: Number(payload.min_stock),
      });
      toast.success("Produit mis à jour");
      setEditProduct(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Supprimer ce produit ?")) return;
    try {
      await api.delete(`/products/${id}`);
      toast.success("Produit supprimé");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const updateMinStock = async (product, value) => {
    const next = Math.max(0, Number(value) || 0);
    if (next === product.min_stock) return;
    try {
      const { id, organization_id, created_at, ...rest } = product;
      await api.put(`/products/${product.id}`, { ...rest, min_stock: next });
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, min_stock: next } : p)));
      toast.success(`Seuil alerte mis à jour : ${product.name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
  };

  const updateField = async (product, field, value) => {
    const next = Math.max(0, Number(value) || 0);
    if (next === product[field]) return;
    try {
      const { id, organization_id, created_at, ...rest } = product;
      await api.put(`/products/${product.id}`, { ...rest, [field]: next });
      setProducts((prev) => prev.map((p) => (p.id === product.id ? { ...p, [field]: next } : p)));
      toast.success(`${field === "price" ? "Prix vente" : "Coût"} mis à jour : ${product.name}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      load();
    }
  };

  const openAdjust = (product) => {
    setAdjustingProduct(product);
    setAdjQty("");
    setAdjSiteId(sites[0]?.id || "");
    setAdjBatchNumber("");
    setAdjExpiry("");
    setAdjWithBatch(false);
    setAdjMode("add");
  };

  const submitAdjust = async (e) => {
    e?.preventDefault();
    if (!adjustingProduct || !adjSiteId) return;
    const qty = Number(adjQty);
    if (qty < 0 || (adjMode !== "set" && qty <= 0)) {
      return toast.error(adjMode === "set"
        ? "Quantité invalide (0 ou plus)"
        : "Saisissez une quantité positive");
    }

    try {
      if (adjMode === "add" && adjWithBatch && adjBatchNumber && adjExpiry) {
        await api.post("/batches", {
          product_id: adjustingProduct.id,
          site_id: adjSiteId,
          batch_number: adjBatchNumber,
          expiry_date: adjExpiry,
          quantity: qty,
          note: "Ajout depuis catalogue",
        });
        toast.success(`+${qty} (lot ${adjBatchNumber}, exp. ${adjExpiry}) ajoutés à ${adjustingProduct.name}`);
      } else if (adjMode === "set") {
        // Compute the delta vs current stock at this site
        const currentAtSite = (stock || [])
          .filter((r) => r.product_id === adjustingProduct.id && r.site_id === adjSiteId)
          .reduce((s, r) => s + (r.quantity || 0), 0);
        const delta = qty - currentAtSite;
        if (delta === 0) {
          toast.info("Le stock est déjà à cette valeur");
          setAdjustingProduct(null);
          return;
        }
        await api.post("/stock/movements", {
          product_id: adjustingProduct.id,
          site_id: adjSiteId,
          type: delta > 0 ? "entree" : "sortie",
          quantity: Math.abs(delta),
          reference: "Ajustement manuel",
          note: `Mise à jour: ${currentAtSite} → ${qty}`,
        });
        toast.success(`Stock défini à ${qty} ${adjustingProduct.unit || ""} (${delta > 0 ? "+" : ""}${delta})`);
      } else if (adjMode === "remove") {
        await api.post("/stock/movements", {
          product_id: adjustingProduct.id,
          site_id: adjSiteId,
          type: "sortie",
          quantity: qty,
          reference: "Retrait manuel",
          note: "",
        });
        toast.success(`-${qty} ${adjustingProduct.unit || ""} retiré(s) de ${adjustingProduct.name}`);
      } else {
        await api.post("/stock/movements", {
          product_id: adjustingProduct.id,
          site_id: adjSiteId,
          type: "entree",
          quantity: qty,
          reference: "Ajout rapide depuis catalogue",
          note: "",
        });
        toast.success(`+${qty} ${adjustingProduct.unit || ""} ajouté(e)s à ${adjustingProduct.name}`);
      }
      setAdjustingProduct(null);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const exportCSV = async () => {
    const { data } = await api.get("/products/export", { responseType: "blob" });
    const url = URL.createObjectURL(new Blob([data], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = "produits.csv"; a.click();
  };

  const exportXLSX = async () => {
    const { data } = await api.get("/products/export-xlsx", { responseType: "blob" });
    const url = URL.createObjectURL(data);
    const a = document.createElement("a");
    a.href = url; a.download = "produits.xlsx"; a.click();
  };

  const importXLSX = async (file) => {
    const fd = new FormData(); fd.append("file", file);
    try {
      const { data } = await api.post("/products/import-xlsx", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.imported} produit(s) importé(s) depuis Excel`);
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const doImport = async () => {
    try {
      const { data } = await api.post("/products/import", { csv: importText });
      toast.success(`${data.imported} produit(s) importé(s)`);
      setShowImport(false); setImportText("");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  // Génération d'images IA en arrière-plan : on POST une fois, le backend
  // lance un job async et on poll le statut. AUCUN risque de timeout Cloudflare.
  const [genImgRunning, setGenImgRunning] = useState(false);
  const [genImgProgress, setGenImgProgress] = useState(null); // {done, total}
  const genImgPollRef = useRef(null);

  const pollImageJobStatus = async () => {
    try {
      const { data } = await api.get("/products/generate-images/status");
      if (data.total > 0 || data.running) {
        setGenImgProgress({ done: data.done || 0, total: data.total || 0 });
      }
      if (!data.running) {
        // Job terminé : on stoppe le polling et on rafraîchit
        clearInterval(genImgPollRef.current);
        genImgPollRef.current = null;
        setGenImgRunning(false);
        const done = data.done || 0;
        const errs = (data.errors || []).length;
        if (data.fatal_error) {
          toast.error(`Job interrompu : ${data.fatal_error}`);
        } else if (done === 0 && errs === 0 && (data.total || 0) === 0) {
          toast.success("Tous les produits ont déjà une image.");
        } else if (errs > 0) {
          toast(`Génération terminée : ${done} image(s) créée(s), ${errs} erreur(s).`);
        } else {
          toast.success(`Génération terminée : ${done} image(s) créée(s).`);
        }
        await load();
      }
    } catch (e) {
      // Si le polling échoue ponctuellement (réseau), on n'arrête pas — le job tourne côté serveur
      console.warn("Polling status échoué:", e?.message);
    }
  };

  const generateMissingImages = async () => {
    if (genImgRunning) return;

    // Étape 1 : nettoyer les image_url qui pointent vers des fichiers cassés
    let scannedReset = 0;
    try {
      const { data: scan } = await api.post("/products/scan-missing-images");
      scannedReset = scan?.reset || 0;
      if (scannedReset > 0) {
        toast(`${scannedReset} image(s) cassée(s) détectée(s) — régénération…`);
      }
      await load();
    } catch (e) {
      console.warn("Scan missing images failed:", e?.message);
    }

    // Étape 2 : compter les produits sans image valide
    const refreshed = await api.get("/products").then((r) => r.data).catch(() => products);
    const missing = refreshed.filter((p) => !p.image_url || p.image_url === "__retry__");
    if (missing.length === 0) {
      toast.success("Tous les produits ont déjà une image valide.");
      return;
    }
    if (!window.confirm(
      `Lancer la génération IA pour ${missing.length} produit(s) sans image valide ?\n\n` +
      `Le job tourne en arrière-plan sur le serveur — vous pouvez fermer cet onglet et revenir plus tard.\n` +
      `Temps estimé : ~${Math.ceil((missing.length * 6) / 60)} minute(s) (3 images en parallèle).`
    )) {
      return;
    }

    // Étape 3 : démarrer le job côté serveur (réponse immédiate)
    setGenImgRunning(true);
    setGenImgProgress({ done: 0, total: missing.length });
    try {
      await api.post("/products/generate-images", { overwrite: false });
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Erreur inconnue";
      toast.error(`Impossible de lancer le job : ${detail}`);
      setGenImgRunning(false);
      return;
    }

    // Étape 4 : poller toutes les 4 s
    if (genImgPollRef.current) clearInterval(genImgPollRef.current);
    genImgPollRef.current = setInterval(pollImageJobStatus, 4000);
    // Premier poll immédiat
    pollImageJobStatus();
  };

  // Au montage : reprendre l'affichage si un job tourne déjà côté serveur
  // (utile si on quitte la page et qu'on revient pendant la génération).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get("/products/generate-images/status");
        if (!cancelled && data?.running) {
          setGenImgRunning(true);
          setGenImgProgress({ done: data.done || 0, total: data.total || 0 });
          if (genImgPollRef.current) clearInterval(genImgPollRef.current);
          genImgPollRef.current = setInterval(pollImageJobStatus, 4000);
        }
      } catch (_) { /* ignore */ }
    })();
    return () => {
      cancelled = true;
      if (genImgPollRef.current) clearInterval(genImgPollRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Synchronisation manuelle des images preview → prod.
  // Génère un JSON de migration côté backend ; l'utilisateur doit ensuite
  // faire "Save to GitHub" + "Re-deploy" pour que la prod l'applique.
  const [syncRunning, setSyncRunning] = useState(false);
  const syncImagesToProd = async () => {
    if (syncRunning) return;
    if (!window.confirm(
      "Synchroniser toutes les images vers la production ?\n\n" +
      "Cette action génère un fichier de migration JSON sur le serveur.\n" +
      "ATTENTION : après ça, vous devrez faire :\n" +
      "1. 'Save to GitHub' (bouton du chat Emergent)\n" +
      "2. 'Re-deploy' depuis l'interface Emergent\n\n" +
      "C'est gratuit (transfert DB → DB, aucun crédit Nano Banana consommé)."
    )) return;
    setSyncRunning(true);
    try {
      const { data } = await api.post("/admin/sync-images-to-prod");
      toast.success(
        `${data.count} image(s) prêtes à pousser en prod (${data.size_mb} MB). ` +
        `Migration : ${data.migration_id}`,
        { duration: 8000 },
      );
      // Affiche les prochaines étapes pendant 12 s
      setTimeout(() => {
        toast(
          "Étape suivante : cliquez sur 'Save to GitHub' dans le chat, puis 'Re-deploy' depuis Emergent.",
          { duration: 12000 },
        );
      }, 1500);
    } catch (e) {
      const detail = e.response?.data?.detail || e.message || "Erreur inconnue";
      toast.error(`Sync échouée : ${detail}`);
    } finally {
      setSyncRunning(false);
    }
  };

  const [initStockRunning, setInitStockRunning] = useState(false);

  // SUPPORT ONLY : régénère le sitemap.xml après ajout de nouvelles catégories.
  const regenerateSitemap = async () => {
    if (!window.confirm("Régénérer sitemap.xml depuis les catégories actuelles ?\n\nUtile après avoir créé des produits dans une nouvelle catégorie.\n\nÉtapes suivantes : Save to GitHub + Re-deploy.")) return;
    try {
      const { data } = await api.post("/admin/regenerate-sitemap");
      toast.success(`Sitemap régénéré : ${data.total_urls} URLs (${data.categories} catégories).`, { duration: 6000 });
      setTimeout(() => {
        toast("Étape suivante : 'Save to GitHub' puis 'Re-deploy' depuis Emergent.", { duration: 8000 });
      }, 1500);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const initStockAll = async () => {
    if (initStockRunning) return;
    if (!window.confirm(
      "Définir le stock de TOUS les produits à 50 unités sur le site principal ?\n\n" +
      "• Pour les produits avec stock = 50 déjà : aucun changement\n" +
      "• Pour les produits avec stock < 50 : ajout du delta nécessaire\n" +
      "• Pour les produits avec stock > 50 : retrait du surplus\n\n" +
      "Action enregistrée dans l'audit. Idempotent (relancer ne double pas)."
    )) return;
    setInitStockRunning(true);
    try {
      const { data } = await api.post("/admin/init-stock", { quantity: 50 });
      toast.success(
        `Stock initialisé à 50 sur "${data.site_name}" : ` +
        `${data.adjusted} produit(s) ajusté(s), ${data.skipped_already_ok} déjà OK.`,
        { duration: 8000 },
      );
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setInitStockRunning(false);
    }
  };

  const categories = useMemo(() => {
    const set = new Set();
    for (const p of products) if (p.category) set.add(p.category);
    return [...set].sort();
  }, [products]);

  const filtered = useMemo(() => {
    const q = filter.trim().toLowerCase();
    return products.filter((p) => {
      if (categoryFilter && p.category !== categoryFilter) return false;
      if (!q) return true;
      return [p.name, p.sku, p.category].join(" ").toLowerCase().includes(q);
    });
  }, [products, filter, categoryFilter]);

  const visible = useMemo(() => filtered.slice(0, visibleCount), [filtered, visibleCount]);

  // Reset pagination when filters change
  useEffect(() => { setVisibleCount(50); }, [filter, categoryFilter]);

  return (
    <div className="space-y-6">
      <PageHeader
        title="Catalogue produits"
        subtitle="Inventaire · SKU"
        testId="products-header"
        actions={canEdit && (
          <>
            <button data-testid="export-products" className="btn-ghost flex items-center gap-2" onClick={exportCSV}><Download size={14}/>CSV</button>
            <button data-testid="export-products-xlsx" className="btn-ghost flex items-center gap-2" onClick={exportXLSX}><Download size={14}/>Excel</button>
            <label data-testid="import-xlsx-label" className="btn-ghost flex items-center gap-2 cursor-pointer">
              <Upload size={14}/>Excel
              <input type="file" accept=".xlsx" className="hidden" onChange={(e)=>e.target.files?.[0] && importXLSX(e.target.files[0])} data-testid="import-xlsx-input"/>
            </label>
            <a data-testid="sample-xlsx" href="/echantillon-produits.xlsx" download className="btn-ghost flex items-center gap-2 text-xs text-[var(--primary-dark)] border-[var(--primary)]"><Download size={14}/>Échantillon 30 médic.</a>
            <button data-testid="open-import" className="btn-ghost flex items-center gap-2" onClick={() => setShowImport(true)}><Upload size={14}/>CSV</button>
            <button data-testid="new-product-btn" className="btn-primary flex items-center gap-2" onClick={() => setShowForm(!showForm)}>
              <Plus size={14}/> Nouveau
            </button>
            {isAdmin && (
              <button
                data-testid="generate-missing-images"
                className="btn-ghost flex items-center gap-2"
                disabled={genImgRunning}
                onClick={generateMissingImages}
                title="Génère des photos IA (Nano Banana) pour les produits qui n'en ont pas encore (rattrapage import Excel/CSV ou anciens produits). Le job tourne en arrière-plan."
              >
                <ImageIcon size={14} />
                {genImgRunning && genImgProgress
                  ? `IA en cours… ${genImgProgress.done}/${genImgProgress.total}`
                  : "Rattrapage IA"}
              </button>
            )}
            {isSupport && (
              <button
                data-testid="sync-images-to-prod"
                className="btn-ghost flex items-center gap-2"
                disabled={syncRunning}
                onClick={syncImagesToProd}
                title="[SUPPORT ONLY] Génère un fichier de migration pour pousser toutes les images vers la prod. Aucun crédit Nano Banana consommé."
              >
                <CloudUpload size={14} />
                {syncRunning ? "Sync en cours…" : "Sync vers prod"}
              </button>
            )}
            {isSupport && (
              <button
                data-testid="init-stock-all"
                className="btn-ghost flex items-center gap-2"
                disabled={initStockRunning}
                onClick={initStockAll}
                title="[SUPPORT ONLY] Définit le stock de tous les produits à 50 unités sur le site principal."
              >
                <PackagePlus size={14} />
                {initStockRunning ? "Init stock…" : "Stock 50/produit"}
              </button>
            )}
            {isSupport && (
              <button
                data-testid="regenerate-sitemap"
                className="btn-ghost flex items-center gap-2"
                onClick={regenerateSitemap}
                title="[SUPPORT ONLY] Régénère sitemap.xml depuis les catégories actuelles (à faire après ajout d'une nouvelle catégorie)."
              >
                <CloudUpload size={14} />
                Sitemap SEO
              </button>
            )}
          </>
        )}
      />

      {/* Adjust stock modal */}
      {adjustingProduct && (
        <div className="fixed inset-0 bg-black/40 z-50 flex items-center justify-center p-4" onClick={() => setAdjustingProduct(null)}>
          <form onSubmit={submitAdjust} onClick={(e) => e.stopPropagation()} className="bg-white rounded-2xl shadow-xl max-w-md w-full p-6 space-y-4" data-testid="adjust-stock-modal">
            <div>
              <div className="font-heading font-extrabold text-xl">
                {adjMode === "add" ? "Ajouter du stock" : adjMode === "remove" ? "Retirer du stock" : "Définir le stock"}
              </div>
              <div className="text-sm text-[var(--text-muted)] mt-1">{adjustingProduct.name} · <span className="font-data">{adjustingProduct.sku}</span></div>
              {(() => {
                const currentAtSite = (stock || [])
                  .filter((r) => r.product_id === adjustingProduct.id && r.site_id === adjSiteId)
                  .reduce((s, r) => s + (r.quantity || 0), 0);
                return (
                  <div className="mt-2 text-xs label-mono inline-flex items-center gap-2 bg-[var(--primary-soft,#F8E8F1)] text-[var(--primary-dark,#8E3A6D)] px-2.5 py-1 rounded-full">
                    Stock actuel sur le site : <span className="font-data font-bold">{currentAtSite} {adjustingProduct.unit || ""}</span>
                  </div>
                );
              })()}
            </div>

            <div className="grid grid-cols-3 gap-1 bg-[var(--bg-soft,#f7f7f7)] p-1 rounded-lg">
              {[
                { k: "add",    label: "Ajouter",  testid: "adj-mode-add" },
                { k: "remove", label: "Retirer",  testid: "adj-mode-remove" },
                { k: "set",    label: "Définir",  testid: "adj-mode-set" },
              ].map((m) => (
                <button
                  key={m.k}
                  type="button"
                  data-testid={m.testid}
                  onClick={() => { setAdjMode(m.k); if (m.k !== "add") setAdjWithBatch(false); }}
                  className={`text-xs py-2 rounded-md font-semibold transition ${
                    adjMode === m.k
                      ? "bg-[var(--primary,#C2569B)] text-white shadow-sm"
                      : "text-[var(--text-muted)] hover:text-[var(--text)]"
                  }`}
                >
                  {m.label}
                </button>
              ))}
            </div>

            <div className="grid grid-cols-1 gap-3">
              <div>
                <label className="label-mono block mb-1.5">Site</label>
                <select data-testid="adj-site" required className="input-base" value={adjSiteId} onChange={(e) => setAdjSiteId(e.target.value)}>
                  <option value="">— Choisir —</option>
                  {sites.map((s) => <option key={s.id} value={s.id}>{s.name}</option>)}
                </select>
              </div>
              <div>
                <label className="label-mono block mb-1.5">
                  {adjMode === "add" && "Quantité à ajouter"}
                  {adjMode === "remove" && "Quantité à retirer"}
                  {adjMode === "set" && "Nouvelle valeur du stock (0 ou plus)"}
                </label>
                <input
                  data-testid="adj-qty"
                  type="number"
                  min={adjMode === "set" ? "0" : "1"}
                  required
                  autoFocus
                  className="input-base"
                  value={adjQty}
                  onChange={(e) => setAdjQty(e.target.value)}
                  placeholder={adjMode === "set" ? "ex. 0 pour rupture" : "ex. 50"}
                />
              </div>

              {adjMode === "add" && (
                <label className="flex items-center gap-2 text-sm cursor-pointer pt-1" data-testid="adj-with-batch-toggle">
                  <input type="checkbox" checked={adjWithBatch} onChange={(e) => setAdjWithBatch(e.target.checked)} className="w-4 h-4 accent-[var(--primary,#C2569B)]" />
                  <span>Tracer ce lot (n° + date de péremption)</span>
                </label>
              )}

              {adjMode === "add" && adjWithBatch && (
                <div className="grid grid-cols-2 gap-3 bg-[var(--bg-soft,#f7f7f7)] p-3 rounded">
                  <div>
                    <label className="label-mono block mb-1.5">N° de lot</label>
                    <input data-testid="adj-batch-number" required={adjWithBatch} className="input-base" value={adjBatchNumber} onChange={(e) => setAdjBatchNumber(e.target.value)} placeholder="LOT-..." />
                  </div>
                  <div>
                    <label className="label-mono block mb-1.5">Péremption</label>
                    <input data-testid="adj-expiry" type="date" required={adjWithBatch} min={new Date().toISOString().slice(0,10)} className="input-base" value={adjExpiry} onChange={(e) => setAdjExpiry(e.target.value)} />
                  </div>
                </div>
              )}

              <div className="text-xs text-[var(--text-muted)] bg-[var(--bg-soft,#f7f7f7)] p-2 rounded">
                {adjMode === "add" && (adjWithBatch
                  ? "Un lot sera créé et apparaîtra dans la page Lots & Péremption."
                  : "Une Entrée sera enregistrée et le stock augmentera de la quantité saisie.")}
                {adjMode === "remove" && "Une Sortie sera enregistrée et le stock diminuera de la quantité saisie."}
                {adjMode === "set" && "Le stock sera ajusté pour correspondre exactement à la valeur saisie (utile pour les corrections d'inventaire ou les ruptures forcées)."}
              </div>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button type="button" className="btn-ghost" onClick={() => setAdjustingProduct(null)}>Annuler</button>
              <button data-testid="submit-adjust" type="submit" className="btn-primary">
                {adjMode === "add" && "Ajouter"}
                {adjMode === "remove" && "Retirer"}
                {adjMode === "set" && "Définir"}
              </button>
            </div>
          </form>
        </div>
      )}

      {showImport && canEdit && (
        <Surface title="Import CSV" testId="import-panel">
          <div className="p-5 space-y-3">
            <p className="label-mono">Format: sku,name,category,unit,price,cost,min_stock</p>
            <textarea data-testid="import-textarea" rows={6} value={importText} onChange={(e) => setImportText(e.target.value)} className="input-base font-data text-xs" placeholder="sku,name,category,unit,price,cost,min_stock&#10;DOLI500,Doliprane 500mg,Antalgique,boîte,5.50,2.10,20" />
            <div className="flex gap-2">
              <button data-testid="do-import" className="btn-primary" onClick={doImport}>Importer</button>
              <button className="btn-ghost" onClick={() => setShowImport(false)}>Annuler</button>
            </div>
          </div>
        </Surface>
      )}

      {showForm && canEdit && (
        <Surface title="Nouveau produit" testId="new-product-form">
          <form onSubmit={submit} className="p-5 grid grid-cols-2 md:grid-cols-3 gap-4">
            <div><label className="label-mono block mb-1.5">SKU</label><input data-testid="form-sku" required className="input-base" value={form.sku} onChange={(e)=>setForm({...form, sku:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Code-barres</label><input data-testid="form-barcode" placeholder="EAN13 (optionnel)" className="input-base" value={form.barcode} onChange={(e)=>setForm({...form, barcode:e.target.value})}/></div>
            <div className="col-span-2"><label className="label-mono block mb-1.5">Nom</label><input data-testid="form-name" required className="input-base" value={form.name} onChange={(e)=>setForm({...form, name:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Catégorie</label><input className="input-base" value={form.category} onChange={(e)=>setForm({...form, category:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Unité</label><input className="input-base" value={form.unit} onChange={(e)=>setForm({...form, unit:e.target.value})}/></div>
            <div><label className="label-mono block mb-1.5">Prix vente (F)</label><input type="number" step="0.01" className="input-base" value={form.price} onChange={(e)=>setForm({...form, price:e.target.value})}/></div>
            {isAdmin && <div><label className="label-mono block mb-1.5">Coût (F)</label><input type="number" step="0.01" className="input-base" value={form.cost} onChange={(e)=>setForm({...form, cost:e.target.value})}/></div>}
            <div><label className="label-mono block mb-1.5">Seuil alerte</label><input type="number" className="input-base" value={form.min_stock} onChange={(e)=>setForm({...form, min_stock:e.target.value})}/></div>
            <div className="col-span-full flex gap-2 pt-2">
              <button data-testid="submit-product" type="submit" className="btn-primary">Créer</button>
              <button type="button" className="btn-ghost" onClick={() => setShowForm(false)}>Annuler</button>
            </div>
          </form>
        </Surface>
      )}

      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[14rem] max-w-md">
          <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--text-muted)]" />
          <input data-testid="products-search" className="input-base pl-9" placeholder="Rechercher SKU, nom, catégorie..." value={filter} onChange={(e)=>setFilter(e.target.value)} />
        </div>
        <select
          data-testid="products-category-filter"
          className="input-base max-w-[14rem]"
          value={categoryFilter}
          onChange={(e) => setCategoryFilter(e.target.value)}
        >
          <option value="">Toutes les catégories</option>
          {categories.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <span className="label-mono">{filtered.length} produit(s){filtered.length > visibleCount && ` · ${visibleCount} affichés`}</span>
      </div>

      {canEdit && filtered.length > 0 && (
        <div className="bg-[var(--primary-soft,#F8E8F1)] border border-[var(--primary,#C2569B)]/30 px-3 py-3 rounded-lg" data-testid="inline-edit-hint">
          <div className="flex items-start gap-2 mb-2">
            <Info size={14} className="text-[var(--primary,#C2569B)] flex-shrink-0 mt-0.5" />
            <div className="text-xs font-semibold text-[var(--text-base,#0a0a0a)]">Comprendre les 2 colonnes :</div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs leading-relaxed pl-5">
            <div className="bg-white/60 rounded px-2 py-1.5">
              <div><b className="text-[var(--primary-dark,#8E3A6D)]">📦 Stock actuel</b> = <i>combien j'ai en réalité</i></div>
              <div className="text-[var(--text-muted)] mt-0.5">→ Cliquer <b>+ Stock</b> pour en ajouter</div>
            </div>
            <div className="bg-white/60 rounded px-2 py-1.5">
              <div><b className="text-[var(--primary-dark,#8E3A6D)]">🔔 Seuil alerte</b> = <i>quand m'avertir ?</i></div>
              <div className="text-[var(--text-muted)] mt-0.5">→ Cliquer la valeur pour modifier</div>
            </div>
          </div>
          <div className="mt-2 pl-5 text-xs text-[var(--text-muted)]">
            <b>Exemple :</b> Doliprane → Stock&nbsp;actuel <span className="font-data text-[var(--alert,#c32a2a)] font-bold">3</span> · Seuil <span className="font-data font-bold">30</span> → <span className="text-[var(--alert,#c32a2a)] font-semibold">CRITIQUE</span> car 3 &lt; 30. Pour résoudre : ajoutez du stock (ex. +50 → stock = 53 &gt; 30 = OK). <span className="italic">Augmenter le seuil ne résout PAS l'alerte, ça la rend plus stricte.</span>
          </div>
        </div>
      )}

      <Surface testId="products-table">
        {filtered.length === 0 ? <Empty message="Aucun produit" /> : (
          <table className="data-table">
            <thead>
              <tr>
                <th>SKU</th><th>Nom</th><th>Catégorie</th><th>Unité</th><th>Prix</th>{isAdmin && <th>Coût</th>}
                <th className="whitespace-nowrap text-right">Stock actuel</th>
                <th className="whitespace-nowrap"><span className="inline-flex items-center gap-1">Seuil alerte <Pencil size={11} className="text-[var(--primary,#C2569B)]"/></span></th>
                {canEdit && <th>Actions</th>}
              </tr>
            </thead>
            <tbody>
              {visible.map((p) => {
                const cur = stockByProduct[p.id] || 0;
                const low = cur < p.min_stock;
                return (
                  <tr key={p.id} data-testid={`product-row-${p.sku}`}>
                    <td className="font-data neon-text-cyan">{p.sku}</td>
                    <td className="font-medium">{p.name}</td>
                    <td className="text-[var(--text-muted)]">{p.category || "—"}</td>
                    <td className="font-data text-[var(--text-muted)]">{p.unit}</td>
                    <td className="font-data">
                      {canEdit ? (
                        <input
                          data-testid={`price-${p.sku}`}
                          type="number" min="0" step="0.01"
                          defaultValue={p.price}
                          onBlur={(e) => updateField(p, "price", e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          title="Cliquez pour modifier · Entrée ou cliquez ailleurs pour sauvegarder"
                          className="input-base w-24 text-right py-1 border-[var(--primary,#C2569B)]/40 hover:border-[var(--primary,#C2569B)] focus:border-[var(--primary,#C2569B)] focus:ring-1 focus:ring-[var(--primary,#C2569B)]/30"
                        />
                      ) : `${p.price.toLocaleString("fr-FR")} F`}
                    </td>
                    {isAdmin && (
                      <td className="font-data">
                        {canEdit ? (
                          <input
                            data-testid={`cost-${p.sku}`}
                            type="number" min="0" step="0.01"
                            defaultValue={p.cost}
                            onBlur={(e) => updateField(p, "cost", e.target.value)}
                            onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                            title="Cliquez pour modifier · Entrée ou cliquez ailleurs pour sauvegarder"
                            className="input-base w-24 text-right py-1 border-[var(--primary,#C2569B)]/40 hover:border-[var(--primary,#C2569B)] focus:border-[var(--primary,#C2569B)] focus:ring-1 focus:ring-[var(--primary,#C2569B)]/30"
                          />
                        ) : `${p.cost.toLocaleString("fr-FR")} F`}
                      </td>
                    )}
                    <td className="font-data text-right" data-testid={`stock-cur-${p.sku}`}>
                      <span className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded ${low ? "bg-[var(--alert-soft,#fde2e2)] text-[var(--alert,#c32a2a)] font-semibold" : ""}`}>
                        {cur}
                        {low && <span className="label-mono text-[10px]">CRITIQUE</span>}
                      </span>
                    </td>
                    <td className="font-data">
                      {canEdit ? (
                        <input
                          data-testid={`min-stock-${p.sku}`}
                          type="number"
                          min="0"
                          defaultValue={p.min_stock}
                          onBlur={(e) => updateMinStock(p, e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") e.target.blur(); }}
                          title="Cliquez pour modifier · puis cliquez ailleurs pour sauvegarder"
                          className="input-base w-20 text-right py-1 border-[var(--primary,#C2569B)]/40 hover:border-[var(--primary,#C2569B)] focus:border-[var(--primary,#C2569B)] focus:ring-1 focus:ring-[var(--primary,#C2569B)]/30"
                        />
                      ) : p.min_stock}
                    </td>
                    {canEdit && (
                      <td>
                        <div className="flex items-center gap-1.5">
                          <button
                            data-testid={`add-stock-${p.sku}`}
                            type="button"
                            onClick={() => openAdjust(p)}
                            disabled={sites.length === 0}
                            className="btn-primary text-xs px-2.5 py-1.5 flex items-center gap-1 disabled:opacity-50"
                            title="Ajouter du stock"
                          >
                            <PackagePlus size={12}/> Stock
                          </button>
                          <label
                            data-testid={`upload-photo-${p.sku}`}
                            className="text-[var(--text-muted)] hover:text-[var(--primary,#C2569B)] transition-colors cursor-pointer relative group"
                            title="Importer une photo (PNG/JPG, max 8 Mo)"
                          >
                            {p.image_url ? (
                              <img
                                src={p.image_url}
                                alt=""
                                className="w-7 h-7 rounded object-cover border border-[var(--border-line)] hover:border-[var(--primary,#C2569B)] transition"
                                onError={(e) => { e.target.style.display = "none"; }}
                              />
                            ) : (
                              <ImageIcon size={14} />
                            )}
                            <input
                              type="file"
                              accept="image/png,image/jpeg,image/webp,image/jpg"
                              className="hidden"
                              onChange={(e) => {
                                const f = e.target.files?.[0];
                                if (f) uploadImage(p, f);
                                e.target.value = "";
                              }}
                            />
                          </label>
                          <button data-testid={`edit-product-${p.sku}`} onClick={() => openEdit(p)} className="text-[var(--text-muted)] hover:text-[var(--primary,#C2569B)] transition-colors" title="Modifier ce produit"><Pencil size={14}/></button>
                          <button data-testid={`del-${p.sku}`} onClick={()=>remove(p.id)} className="text-[var(--text-muted)] hover:text-[var(--neon-pink)] transition-colors" title="Supprimer"><Trash2 size={14}/></button>
                        </div>
                      </td>
                    )}
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
        {filtered.length > visibleCount && (
          <div className="flex items-center justify-center gap-3 py-4 border-t border-[var(--border-line)]">
            <div className="text-xs text-[var(--text-muted)]">
              {visibleCount} / {filtered.length} produits affichés
            </div>
            <button
              data-testid="load-more-products"
              type="button"
              onClick={() => setVisibleCount((n) => n + 100)}
              className="btn-secondary text-xs px-4 py-1.5"
            >
              Afficher 100 de plus
            </button>
            <button
              type="button"
              onClick={() => setVisibleCount(filtered.length)}
              className="btn-ghost text-xs px-4 py-1.5"
            >
              Tout afficher
            </button>
          </div>
        )}
      </Surface>

      {editProduct && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-start sm:items-center justify-center p-4" onClick={() => setEditProduct(null)}>
          <form
            onSubmit={submitEdit}
            onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl shadow-2xl max-w-lg w-full p-6 space-y-4 max-h-[90vh] overflow-y-auto"
            data-testid="edit-product-modal"
          >
            <div>
              <div className="font-heading font-extrabold text-xl">Modifier le produit</div>
              <div className="text-xs text-[var(--text-muted)] mt-1">Tous les champs sont modifiables, y compris le SKU.</div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <label className="label-mono block mb-1.5">Nom du produit *</label>
                <input data-testid="edit-product-name" required className="input-base" value={editProduct.name} onChange={(e) => setEditProduct({...editProduct, name: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">SKU *</label>
                <input data-testid="edit-product-sku" required className="input-base" value={editProduct.sku} onChange={(e) => setEditProduct({...editProduct, sku: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Code-barres</label>
                <input data-testid="edit-product-barcode" className="input-base" value={editProduct.barcode} onChange={(e) => setEditProduct({...editProduct, barcode: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Catégorie</label>
                <input data-testid="edit-product-category" className="input-base" value={editProduct.category} onChange={(e) => setEditProduct({...editProduct, category: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Unité</label>
                <input data-testid="edit-product-unit" className="input-base" value={editProduct.unit} onChange={(e) => setEditProduct({...editProduct, unit: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Prix vente (FCFA)</label>
                <input data-testid="edit-product-price" type="number" min="0" step="any" className="input-base" value={editProduct.price} onChange={(e) => setEditProduct({...editProduct, price: e.target.value})}/>
              </div>
              <div>
                <label className="label-mono block mb-1.5">Coût (FCFA)</label>
                <input data-testid="edit-product-cost" type="number" min="0" step="any" className="input-base" value={editProduct.cost} onChange={(e) => setEditProduct({...editProduct, cost: e.target.value})}/>
              </div>
              <div className="col-span-2">
                <label className="label-mono block mb-1.5">Seuil alerte stock</label>
                <input data-testid="edit-product-minstock" type="number" min="0" className="input-base" value={editProduct.min_stock} onChange={(e) => setEditProduct({...editProduct, min_stock: e.target.value})}/>
              </div>
            </div>
            <div className="flex gap-2 pt-2">
              <button data-testid="save-edit-product" type="submit" className="btn-primary">Enregistrer</button>
              <button type="button" className="btn-ghost" onClick={() => setEditProduct(null)}>Annuler</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
